import { projectId, publicAnonKey } from '/utils/supabase/info';
import type { ActivityEvent } from './adaptive-experience';
import { withRetry } from './fetch-retry';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

/** Max events per POST (must match server). */
export const ADAPTIVE_EVENTS_MAX_BATCH = 50;

function authHeaders(accessToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${publicAnonKey}`,
    'X-User-Token': accessToken,
  };
}

async function fetchOnce(accessToken: string, limit: number): Promise<ActivityEvent[]> {
  const res = await fetch(`${API_BASE}/adaptive-experience/events?limit=${limit}`, {
    headers: authHeaders(accessToken),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : 'Failed to load adaptive events');
  const raw = Array.isArray(data?.events) ? data.events : [];
  return raw
    .map((row: { clientEventId?: string; type?: string; at?: number; meta?: Record<string, unknown> }) => {
      if (!row?.clientEventId || typeof row.type !== 'string' || typeof row.at !== 'number') return null;
      const ev: ActivityEvent = {
        clientEventId: row.clientEventId,
        type: row.type,
        at: row.at,
        ...(row.meta && typeof row.meta === 'object' ? { meta: row.meta } : {}),
      };
      return ev;
    })
    .filter((e: ActivityEvent | null): e is ActivityEvent => e != null);
}

/** Load persisted adaptive events for the signed-in user (cross-device). */
export async function fetchAdaptiveExperienceEvents(accessToken: string, limit = 200): Promise<ActivityEvent[]> {
  return withRetry(() => fetchOnce(accessToken, limit), {
    maxAttempts: 4,
    baseDelayMs: 400,
    shouldRetry: () => true,
  });
}

async function pushOnce(accessToken: string, batch: ActivityEvent[]): Promise<number> {
  const payload = batch
    .filter((e) => typeof e.clientEventId === 'string' && e.clientEventId.length > 0)
    .map((e) => ({
      clientEventId: e.clientEventId as string,
      type: e.type,
      at: e.at,
      meta: e.meta ?? {},
    }));
  if (payload.length === 0) return 0;

  const res = await fetch(`${API_BASE}/adaptive-experience/events`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ events: payload }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : 'Failed to sync adaptive events');
  return typeof data?.inserted === 'number' ? data.inserted : 0;
}

/** Idempotent batch upload (server skips duplicate client_event_id). Chunks to ADAPTIVE_EVENTS_MAX_BATCH. */
export async function pushAdaptiveExperienceEvents(accessToken: string, events: ActivityEvent[]): Promise<number> {
  let total = 0;
  for (let i = 0; i < events.length; i += ADAPTIVE_EVENTS_MAX_BATCH) {
    const chunk = events.slice(i, i + ADAPTIVE_EVENTS_MAX_BATCH);
    const inserted = await withRetry(() => pushOnce(accessToken, chunk), {
      maxAttempts: 4,
      baseDelayMs: 500,
      shouldRetry: () => true,
    });
    total += inserted;
  }
  return total;
}

/** Delete all server-side adaptive events for the current user. */
export async function deleteAllAdaptiveExperienceEvents(accessToken: string): Promise<void> {
  const res = await fetch(`${API_BASE}/adaptive-experience/events`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : 'Failed to delete adaptive events');
}
